import { CLOUD_API_URL } from '../config/serverConfig';
import { withPaidCheckoutConsent } from './paidCheckoutConsent';
import { formatBillingError } from '../services/subscriptionService';
import { isNativeApp, isIosShell } from './platform';
import { isIosCommerceBlocked } from './iosCommerceGate';
import { STRIPE_BROWSER_FALLBACK_URL } from './stripeLoader';
import { shouldIgnoreAlreadySubscribed, shouldShowAlreadySubscribed } from './billingEntitlement';

/** Native WebViews should use Stripe hosted checkout in the system browser. iOS shell blocks all commerce (#408). */
export function shouldPreferBrowserCheckout(): boolean {
  if (isIosShell()) return false;
  return isNativeApp();
}

export const NATIVE_SHELL_SUBSCRIBE_PATH = '/subscribe';

function getStoredAccessToken(): string | null {
  try {
    const raw = localStorage.getItem('maestro_auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { accessToken?: string };
    return parsed.accessToken || null;
  } catch {
    return null;
  }
}

export function buildSubscribePath(query: Record<string, string> = {}): string {
  const params = new URLSearchParams(query);
  const qs = params.toString();
  return qs ? `${NATIVE_SHELL_SUBSCRIBE_PATH}?${qs}` : NATIVE_SHELL_SUBSCRIBE_PATH;
}

/** In-shell navigation to /subscribe — safe after native marketing allowlist (#325). Blocked on iOS (#408). */
export function navigateToSubscribe(query: Record<string, string> = {}): void {
  if (isIosCommerceBlocked()) {
    window.location.href = '/app';
    return;
  }
  window.location.href = buildSubscribePath(query);
}

export async function openUrlInSystemBrowser(url: string): Promise<boolean> {
  const target = String(url || '').trim();
  if (!target) return false;

  try {
    const w = window as Window & {
      __TAURI__?: { core?: { invoke?: (cmd: string, args: Record<string, unknown>) => Promise<unknown> }; invoke?: (cmd: string, args: Record<string, unknown>) => Promise<unknown> };
      __maestroNative?: { openURL?: (url: string) => void };
      webkit?: { messageHandlers?: { maestroNative?: { postMessage?: (msg: unknown) => void } } };
    };

    const core = w.__TAURI__?.core || w.__TAURI__;
    if (typeof core?.invoke === 'function') {
      await core.invoke('plugin:shell|open', { path: target });
      return true;
    }

    if (typeof w.__maestroNative?.openURL === 'function') {
      w.__maestroNative.openURL(target);
      return true;
    }

    const post = w.webkit?.messageHandlers?.maestroNative?.postMessage;
    if (typeof post === 'function') {
      post({ action: 'openURL', url: target });
      return true;
    }
  } catch (e) {
    console.error('[billingHandoff] system browser open failed:', e);
  }

  try {
    const anchor = document.createElement('a');
    anchor.href = target;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    return true;
  } catch (e) {
    console.error('[billingHandoff] anchor fallback failed:', e);
    return false;
  }
}

export type HostedCheckoutResult =
  | { ok: true; url: string; opened: boolean; alreadySubscribed?: boolean; message?: string }
  | { ok: false; error: string; alreadySubscribed?: boolean; message?: string };

export async function startHostedCheckout(cadence: string = 'monthly'): Promise<HostedCheckoutResult> {
  if (isIosCommerceBlocked()) {
    return { ok: false, error: 'Subscription management is available at maestrodesktop.com from a computer.' };
  }
  const token = getStoredAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let subContext: {
    subscription_tier?: string;
    had_trial?: boolean;
    has_payment_method?: boolean;
    subscribe_via_checkout?: boolean;
    resubscribe_via_checkout?: boolean;
    stripe_customer_id?: string | null;
    stripe_subscription_id?: string | null;
    has_stripe_subscription?: boolean;
  } | null = null;
  if (token) {
    try {
      const statusResp = await fetch(`${CLOUD_API_URL}/api/stripe/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'omit',
      });
      if (statusResp.ok) {
        subContext = await statusResp.json();
      }
    } catch { /* non-critical */ }
  }

  try {
    const resp = await fetch(`${CLOUD_API_URL}/api/stripe/create-checkout-session`, {
      method: 'POST', headers, credentials: 'omit',
      body: JSON.stringify(withPaidCheckoutConsent({ ui_mode: 'hosted', cadence, trial_pitch: subContext?.subscription_tier === 'trial' }, !!token)),
    });
    const data = await resp.json().catch(() => ({})) as {
      url?: string;
      already_subscribed?: boolean;
      ok?: boolean;
      message?: string;
      error?: string;
    };

    if (resp.ok && data.already_subscribed && data.ok) {
      if (shouldIgnoreAlreadySubscribed(subContext)) {
        return {
          ok: false,
          error: formatBillingError(data, 'Could not start checkout. Please try again.'),
        };
      }
      if (!shouldShowAlreadySubscribed(subContext)) {
        return {
          ok: false,
          error: formatBillingError(data, 'Could not start checkout. Please try again.'),
        };
      }
      return {
        ok: true,
        url: STRIPE_BROWSER_FALLBACK_URL,
        opened: false,
        alreadySubscribed: true,
        message: data.message || "You're already subscribed to Maestro.",
      };
    }

    if (data.url) {
      const opened = await openUrlInSystemBrowser(data.url);
      return { ok: true, url: data.url, opened };
    }

    return {
      ok: false,
      error: formatBillingError(data, 'Could not start checkout. Please try again.'),
    };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'network error';
    return { ok: false, error: message || 'Connection failed. Please try again.' };
  }
}

export async function openSubscribeInBrowser(): Promise<boolean> {
  return openUrlInSystemBrowser(STRIPE_BROWSER_FALLBACK_URL);
}
